// src/pages/CatProfile.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function CatProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    getDoc(doc(db, "cats", id)).then(snap => {
      if (snap.exists()) setCat({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  if (loading) return <div style={styles.loading}><div className="spinner"/></div>;
  if (!cat) return <div style={styles.loading}><p>Cat not found.</p><Link to="/" style={{color:"var(--rust)"}}>← Back</Link></div>;

  const photos = cat.photos || [];
  const isAvailable = cat.status === "available";

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Back */}
        <button onClick={()=>navigate(-1)} style={styles.back}>← Back to Browse</button>

        <div style={styles.layout}>
          {/* Photos */}
          <div style={styles.photosCol}>
            <div style={styles.mainPhoto}>
              {photos[activePhoto] ? (
                <img src={photos[activePhoto]} alt={cat.name} style={styles.mainImg}/>
              ) : (
                <div style={styles.noPhoto}>🐱</div>
              )}
              {cat.status === "adopted" && (
                <div style={styles.adoptedOverlay}>Adopted 🎉</div>
              )}
            </div>
            {photos.length > 1 && (
              <div style={styles.thumbnails}>
                {photos.map((p, i) => (
                  <div key={i}
                    style={{...styles.thumb, ...(activePhoto===i ? styles.thumbActive:{})}}
                    onClick={()=>setActivePhoto(i)}>
                    <img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div style={styles.infoCol}>
            <div style={styles.nameRow}>
              <h1 style={styles.name}>{cat.name}</h1>
              <span style={styles.gender}>{cat.gender === "male" ? "♂ Male" : "♀ Female"}</span>
            </div>

            <div style={styles.metaRow}>
              <Pill icon="🎂" label={cat.age || "Unknown age"} />
              <Pill icon="🐾" label={cat.breed || "Mixed breed"} />
              <Pill icon="📍" label={cat.location || "Malaysia"} />
            </div>

            <div style={styles.statusRow}>
              <span style={{
                ...styles.statusBadge,
                background: isAvailable ? "#e8f5ee" : "#f5e8e8",
                color: isAvailable ? "#2e7d52" : "#7d2e2e"
              }}>
                {isAvailable ? "✓ Available for Adoption" : cat.status === "pending" ? "⏳ Pending" : "❤️ Adopted"}
              </span>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>About {cat.name}</h3>
              <p style={styles.desc}>{cat.description}</p>
            </div>

            {cat.traits?.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Personality</h3>
                <div style={styles.traits}>
                  {cat.traits.map(t => <span key={t} className="tag" style={{fontSize:14,padding:"6px 14px"}}>{t}</span>)}
                </div>
              </div>
            )}

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Health & Care</h3>
              <div style={styles.healthGrid}>
                <HealthItem label="Vaccinated" value={cat.vaccinated} />
                <HealthItem label="Neutered/Spayed" value={cat.neutered} />
                <HealthItem label="Microchipped" value={cat.microchipped} />
                <HealthItem label="Litter Trained" value={cat.litterTrained} />
              </div>
            </div>

            {isAvailable && (
              <button
                onClick={() => navigate(`/adopt/${cat.id}`)}
                style={styles.adoptBtn}>
                🐾 I Want to Adopt {cat.name}
              </button>
            )}

            {!isAvailable && (
              <div style={styles.notAvailable}>
                This cat has already found a home. <Link to="/" style={{color:"var(--rust)"}}>Browse other cats →</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill({ icon, label }) {
  return (
    <span style={{display:"flex",alignItems:"center",gap:6,background:"var(--warm)",
      padding:"6px 14px",borderRadius:50,fontSize:14,color:"var(--mid)"}}>
      {icon} {label}
    </span>
  );
}

function HealthItem({ label, value }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",
      borderBottom:"1px solid var(--warm)"}}>
      <span style={{fontSize:16}}>{value ? "✅" : "❌"}</span>
      <span style={{fontSize:14,color:"var(--text)"}}>{label}</span>
    </div>
  );
}

const styles = {
  page: { minHeight:"100vh", padding:"32px 0 80px" },
  container: { maxWidth:1000, margin:"0 auto", padding:"0 24px" },
  loading: { padding:80, textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:16 },
  back: { background:"none", border:"none", color:"var(--muted)", fontSize:15, cursor:"pointer", marginBottom:28, padding:0 },

  layout: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, "@media(max-width:700px)":{gridTemplateColumns:"1fr"} },

  photosCol: {},
  mainPhoto: {
    position:"relative", borderRadius:20, overflow:"hidden",
    height:400, background:"var(--warm)", marginBottom:12,
    boxShadow:"var(--shadow-m)",
  },
  mainImg: { width:"100%", height:"100%", objectFit:"cover" },
  noPhoto: { width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:80 },
  adoptedOverlay: {
    position:"absolute", inset:0, background:"rgba(30,19,10,0.6)",
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:28, fontFamily:"'Playfair Display',serif", color:"#fff", fontWeight:700,
  },
  thumbnails: { display:"flex", gap:10, flexWrap:"wrap" },
  thumb: { width:72, height:72, borderRadius:10, overflow:"hidden", cursor:"pointer", opacity:0.6, border:"2px solid transparent", transition:"all 0.2s" },
  thumbActive: { opacity:1, borderColor:"var(--rust)" },

  infoCol: { display:"flex", flexDirection:"column", gap:0 },
  nameRow: { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 },
  name: { fontFamily:"'Playfair Display',serif", fontSize:40, color:"var(--deep)" },
  gender: { fontSize:16, color:"var(--muted)", fontWeight:500 },
  metaRow: { display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 },
  statusRow: { marginBottom:24 },
  statusBadge: { padding:"8px 18px", borderRadius:50, fontSize:14, fontWeight:600 },

  section: { marginBottom:24 },
  sectionTitle: { fontFamily:"'Playfair Display',serif", fontSize:18, color:"var(--deep)", marginBottom:10 },
  desc: { fontSize:15, lineHeight:1.7, color:"var(--text)" },
  traits: { display:"flex", gap:8, flexWrap:"wrap" },
  healthGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 },

  adoptBtn: {
    background:"var(--rust)", color:"#fff",
    padding:"16px 32px", borderRadius:50,
    fontSize:17, fontWeight:600,
    border:"none", cursor:"pointer",
    transition:"all 0.2s",
    marginTop:8, textAlign:"center",
    boxShadow:"0 6px 24px rgba(192,98,42,0.35)",
  },
  notAvailable: { padding:16, background:"var(--warm)", borderRadius:12, fontSize:15, color:"var(--muted)" },
};
